/**
 * One interruption per session (product UX plan step 13, owner decision
 * 5, 2026-10-29). Before it the screen was shared by eight prompts; a
 * phone session met the install sheet and then the feedback offer before
 * its first tap, and one account saw the feedback offer 29 times in a
 * week.
 *
 * The rules:
 *   - the cookie note comes first: while it is on screen nothing else
 *     comes up;
 *   - after it a session gets at most one offer: the install sheet, the
 *     feedback offer, the trial sheet or a coach mark (the pulse and
 *     tooltip that point once at a control). The first to come up uses
 *     the session; the others wait for the next one;
 *   - when more than one waits, a fixed order decides (PRIORITY), not who
 *     asked first; a prompt on screen keeps its place, except a coach
 *     mark, which gives way to the welcome card or the team's reply;
 *   - never on the session's first page view, and never over something
 *     the person has open: a result (or an analysis on its way) and a
 *     drawer hold every offer back (`hold`);
 *   - one floating prompt on screen at a time (the slot);
 *   - the welcome card and the team's reply to a ticket answer something
 *     the person did (a sign-up, their own message). They need no unused
 *     session and may come on its first page view, but the cookie note
 *     still comes first, and the reply also waits for a result or a
 *     drawer to close (the welcome card does not: it answers the sign-up
 *     made on that very page). They take the slot like the others and
 *     use up the session, so no offer follows them;
 *   - the install card under a result covers nothing: it needs no slot,
 *     only the session, which it uses once it has been scrolled into view.
 *
 * A session is the analytics one (lib/tracking: 30 minutes idle, shared
 * by every tab); the TrackingProvider reports each page view. What the
 * session has used up lives in localStorage, so every tab sees it; who
 * holds the slot lives in this tab. Storage we cannot read or write is an
 * unknown session, and an unknown session gets no offer: better none than
 * one we cannot remember having made.
 *
 * Free of React so the unit tests drive it directly; the React side is
 * lib/floating-slot.
 */

/** Wait for the budget. */
export type Offer = "install" | "feedback" | "trial";
/** Coach marks: wait for the budget like the offers, below all of them.
 *  The map's layer switcher and the listings' saved-search button. */
export type Hint = "hint-layers" | "hint-saved";
/** Answer something the person did: no budget needed. */
export type Answer = "welcome" | "thread";
/** On screen above the page: one at a time. */
export type Floating = Offer | Hint | Answer;
/** In the page's flow (the install card under a result). */
export type Inline = "install-card";
export type Prompt = Floating | Inline;

const HINTS: readonly Prompt[] = ["hint-layers", "hint-saved"];

/**
 * The fixed order. When more than one prompt waits for the screen, the
 * first of these that may come up gets it, whichever asked first:
 *   - the answers first: they reply to something the person just did;
 *   - the install sheet: once per browser, so it costs one session;
 *   - the feedback offer (at most 6 shows per account), then the trial
 *     sheet (once ever);
 *   - the coach marks last: help nobody asked for, and the only prompts
 *     that give way to an answer once on screen.
 */
export const PRIORITY: readonly Floating[] = [
  "welcome", "thread", "install", "feedback", "trial", "hint-layers", "hint-saved",
];

/** localStorage: the session this browser is in and what it has used up. */
export const SESSION_KEY = "menzil.interrupt";

export interface SessionMark {
  /** The analytics session id (lib/tracking). */
  sid: string;
  /** Page views so far in the session, in every tab. */
  views: number;
  /** The prompt that used up the session's interruption (a name another
   *  build may not know counts the same). */
  spent: string | null;
}

/** What a decision reads. */
export interface BudgetView {
  /** This tab's page in the session: 1 for its first page view; null
   *  when unknown. */
  view: number | null;
  /** Who used up this tab's session (null: nobody yet); undefined when
   *  the session is unknown. */
  spent: string | null | undefined;
  /** Something is open that nothing may cover: a result, an analysis on
   *  its way, a drawer. */
  held: boolean;
  /** The cookie note is on screen: it comes before every prompt. */
  cookie?: boolean;
}

/** Whether a prompt may come up now. */
export function mayComeUp(who: Prompt, v: BudgetView): boolean {
  // In the page's flow, it covers nothing: only the session counts.
  if (who === "install-card") return v.spent === null || v.spent === who;
  if (v.cookie) return false;
  if (who === "welcome") return true;
  if (who === "thread") return !v.held;
  if (v.spent === undefined || (v.spent !== null && v.spent !== who)) return false;
  return v.view !== null && v.view >= 2 && !v.held;
}

/** The session after one more page view in ``sid``. */
export function nextMark(prev: SessionMark | null, sid: string): SessionMark {
  return prev && prev.sid === sid
    ? { sid, views: prev.views + 1, spent: prev.spent }
    : { sid, views: 1, spent: null };
}

function readMark(): SessionMark | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const m = JSON.parse(raw) as Partial<SessionMark> | null;
    if (!m || typeof m.sid !== "string" || typeof m.views !== "number" || !Number.isFinite(m.views)) return null;
    return { sid: m.sid, views: m.views, spent: typeof m.spent === "string" ? m.spent : null };
  } catch {
    return null;
  }
}

function writeMark(m: SessionMark): boolean {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(m));
    return true;
  } catch {
    return false;
  }
}

export interface BudgetSnapshot {
  /** The floating prompt that holds the slot. */
  owner: Floating | null;
  /** Who used up this tab's session; undefined when unknown. */
  spent: string | null | undefined;
}

export const SERVER_SNAPSHOT: BudgetSnapshot = { owner: null, spent: undefined };

/** This tab's page: its session and its place in it. */
let page: { sid: string; view: number } | null = null;
let owner: Floating | null = null;
/** Floating prompts that want the slot, with how many of their copies
 *  ask (the map draws its layer switcher twice, one per screen size). */
const wants = new Map<Floating, number>();
let holds = 0;
let cookieHolds = 0;
let snapshot: BudgetSnapshot = SERVER_SNAPSHOT;
const listeners = new Set<() => void>();

function current(): BudgetView {
  const mark = page ? readMark() : null;
  return {
    view: page ? page.view : null,
    spent: page && mark && mark.sid === page.sid ? mark.spent : undefined,
    held: holds > 0,
    cookie: cookieHolds > 0,
  };
}

/** ``who`` uses up the session, unless somebody did already. */
function takeSession(who: Prompt): void {
  if (!page) return;
  const mark = readMark();
  if (mark && mark.sid === page.sid && mark.spent === null) writeMark({ ...mark, spent: who });
}

/** Hands the slot to whoever may have it now, and tells the listeners. */
function settle(): void {
  let v = current();
  // A hold takes the slot back from an offer; it returns when it ends.
  if (owner !== null && !mayComeUp(owner, v)) owner = null;
  // A coach mark gives way to a waiting prompt above it. In practice an
  // answer: no offer may come up in the session the mark has used.
  if (owner !== null && HINTS.includes(owner)) {
    const rank = PRIORITY.indexOf(owner);
    if (PRIORITY.some((w, i) => i < rank && wants.has(w) && mayComeUp(w, v))) owner = null;
  }
  if (owner === null) {
    const next = PRIORITY.find((w) => wants.has(w) && mayComeUp(w, v));
    if (next) {
      owner = next;
      takeSession(next);
      v = current();
    }
  }
  if (snapshot.owner !== owner || snapshot.spent !== v.spent) {
    snapshot = { owner, spent: v.spent };
    listeners.forEach((l) => l());
  }
}

/** A page view in the analytics session ``sid`` (null: no session we can
 *  keep, so none of this tab's pages counts as known). */
export function notePageView(sid: string | null): void {
  page = null;
  if (sid) {
    const next = nextMark(readMark(), sid);
    if (writeMark(next)) page = { sid, view: next.views };
  }
  settle();
}

/** A floating prompt wants the slot (its own rules passed). Each call is
 *  undone by one ``drop``: a prompt drawn twice waits until both copies
 *  have let go. */
export function want(who: Floating): void {
  wants.set(who, (wants.get(who) ?? 0) + 1);
  settle();
}

/** It no longer does (closed, answered, gone). */
export function drop(who: Floating): void {
  const left = (wants.get(who) ?? 0) - 1;
  if (left > 0) {
    wants.set(who, left);
  } else {
    wants.delete(who);
    if (owner === who) owner = null;
  }
  settle();
}

/** What holds the prompts back: something the person has open (a
 *  result, an analysis on its way, a drawer) or the cookie note. */
export type HoldKind = "open" | "cookie";

/** Something nothing may cover is on screen, until the returned release
 *  runs. */
export function hold(kind: HoldKind = "open"): () => void {
  const add = (n: number) => {
    if (kind === "cookie") cookieHolds += n;
    else holds += n;
  };
  add(1);
  settle();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    add(-1);
    settle();
  };
}

/** An inline prompt has been seen: it uses up the session if it may. */
export function spend(who: Inline): void {
  if (mayComeUp(who, current())) takeSession(who);
  settle();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getSnapshot(): BudgetSnapshot {
  return snapshot;
}

/** @public (the unit tests exercise it directly) */
export function resetForTests(): void {
  page = null;
  owner = null;
  wants.clear();
  holds = 0;
  cookieHolds = 0;
  snapshot = SERVER_SNAPSHOT;
  listeners.clear();
}
